// src/utils/apiPath.js
//
// This file imports NOTHING, on purpose.
//
// It used to pull in date-fns, ../redux/auth/authActions, ../pages/ShiftGroup
// and ../pages/EmployeeMaster — none of them used, all four almost certainly
// left behind by an editor's auto-import. Three of them closed a cycle:
//
//   apiPath.js -> authActions -> authApi -> api.js -> apiPath.js
//
// api.js reads apiPath.API_URL at module scope to build its axios instance.
// Whenever the bundler happened to enter that ring at api.js, it read the
// `export const apiPath` binding while apiPath.js was still mid-evaluation and
// threw "Cannot access 'apiPath' before initialization" — a blank screen, from
// four imports nothing referenced. The order the ring is entered in shifts
// with any new import anywhere in the graph, which is why it could sit quiet
// for a long time and then break on an unrelated change.
//
// Importing a PAGE from a config file is the tell: nothing here needs a React
// component. Keep this a leaf — plain strings, no imports.

// 1. Get the base URL (or use a hardcoded fallback for local dev)
const envUrl = process.env.REACT_APP_API_LOCAL_URL || "https://skyeye.swasinfotechnologies.cloud/";

// 2. Ensure it ends with a slash to prevent "localhost:9000api" errors
const baseUrl = envUrl.endsWith("/") ? envUrl : `${envUrl}/`;

export const apiPath = {
  // Result: "http://localhost:9000/api/v1"
  API_URL: `${baseUrl}api/v1`,

  // Auth Endpoints
  authLogin: "/auth/login", // Added leading slash for clarity
  googleLogin: "/auth/google-login",
  verifyOtp: "/auth/verify-otp",
  resendOtp: "/auth/resend-otp",

  // User Endpoints
  users: {
    createUser: "/user/create",
    getUser: "/user/list",
    updatePassword: "/user/password/update",
    deleteUser: "/user/delete",
    updateWorkStatus: "/user/update-user-work-status"
  },

  tasks: {
    addTask: "/tasks/create",
    getTask: "/tasks/list",
    projectMenu: "/master/project",
    queryFrom: "/master/query-from",
    clientMenu: "/master/client",
    mainMenuApi: "/master/menu",
    submenu1Api: "/master/submenu1",
    submenu2Api: "/master/submenu2",
    submenu3Api: "/master/submenu3",
    submenu4Api: "/master/submenu4",
    tasksAssign: "/tasks-assignment/assign",
    updateDevelopmentTask: "/tasks-development/update",
    createStatusTracker: "/tasks-development/create-status-tracker",
    getTaskAssignment: "/tasks-assignment/list",
    getTaskDevelopment: "/tasks-development/list",
    raisedbyDetails: "/tasks/raisedby-details",
    lastEndTime: "/tasks/last-end-time",
    supportTeamTaskCreate: "/tasks/support-team-task-create",
    supportTeamTaskList: "/tasks/support-team-task-list",
    historyList: "/tasks-development/history-tracker-list",
    /* Who spent the time on one task, split per person. */
    workedTimeByUser: "/tasks-development/worked-time-by-user",
  },

  /* Screenshots attached to a task — at most two, stored in tbl_QueryImage.
     `file` takes the image code and answers with the bytes, so it is fetched
     as a blob rather than read out of a JSON envelope. */
  taskImage: {
    list: "/task-image/list",
    add: "/task-image",
    file: (code) => `/task-image/${code}`,
    remove: (code) => `/task-image/${code}`,
  },

  team: {
    list: "/team/list",
    members: "/team/members",
    flow: "/team/flow",
    move: "/team/move",
    timeline: "/team/timeline",
    member: "/team/member",
    /* Who somebody sits under, inside one team. Admin only. */
    memberManager: "/team/member/manager",
    /* The list of positions. Readable by everyone — the chart labels every
       card with one. */
    designations: "/team/designations",
    /* What one person does. Admin only. */
    designation: "/team/designation",
    /* POST creates, or renames when a teamCode is sent. Admin only. */
    upsert: "/team",
    /* PUT the whole ordered list of team codes. Admin only. */
    order: "/team/order",
    userTeams: "/team/user-teams",
  },

  report: {
    reportList: "/report/list",
    userReportList: "/report/user-report-list",
    staffAttendance: "/report/staff-attendance",
    clientQueryStatus: "/report/client-query-status",
    excelClientWiseReportCount: "/report/excel-client-report",
  },

  testing: {
    testingList: "/testing-task/list",
  },

  deployements: {
    deployementsList: "/deployements-task/list",
  },

  dashboard: {
    clientStatus: "/dashboard/client-status",
    queryType: "/dashboard/query-type",
    summary: "/dashboard/summary",
    summaryByProject: "/dashboard/summary-by-project",
    userStatus: "/dashboard/user-status",
    punchSpells: "/dashboard/punch-spells",
    activeTasks: "/dashboard/active-tasks",
    yesterdayProductivityStar: "/dashboard/queries-count-and-productivity-star",
    attendacneList: "/dashboard/list-punching-details",
    clock_status: "/dashboard/clock-status",
    /* The whole team's clock, not just yours — clock_status above backs the
       header button and answers for one person. */
    teamClockStatus: "/dashboard/team-clock-status",
    /* Break Hours split by reason (Break / Lunch / Be right back). */
    breakBreakdown: "/dashboard/break-breakdown",
    /* Attendance per person per day, away time split by reason. */
    punchingReport: "/dashboard/punching-report",
    teamWise: "/dashboard/team-wise",
    /* The rows behind one team's pending count on the dashboard. */
    teamPendingTasks: "/dashboard/team-pending-tasks",

  },

  ai: {
    chat: "/ai/chat",
    status: "/ai/status",
    recent: "/ai/recent",
  },
  shiftGroup: {
    list: "/master/list-shiftgroup",
    create: "/master/shiftgroup",
    getShiftGroupById: "/master/get-Shift-Group",
    update: "/master/update-shiftgroup",
    delete: "/master/delete-shiftgroup",
    delete_shift: "/master/delete-message-shiftgroup",
    shiftlist: "/master/list-shift",
    create_list: "/master/shift",
    getShiftById: "/master/get-shift",
    updateShift: "/master/update-shift",
    delete_list: "/master/delete-shift",
    delete_list_message: "/master/delete-message-shift",
  },
  clockin: {
    clockIn: "/attendance/clock-in",
    clockOut: "/attendance/clock-out",
    /* Page load calls this, not clockIn: it undoes the tab-close punch when
       the app has just come back, so a refresh spends no punches. */
    resumeSession: "/attendance/resume-session",
    workingHours: "/attendance/working-hours"
  },
  ManualEntry: {
    add: "/attendance/manual",
    list: "/attendance/list-manual",
    update: "/attendance/update-manual",
    getUpdate: "/attendance/get-manual",
    deletemsg: "/attendance/delete-message-manual",
    delete: "/attendance/delete-manual",
    ShiftTiming: "/master/shift-timing",
  },
  status: {
    status_list: "/master/list-status",
    status_create: "/master/create-status",
    getStatusById: "/master/get-status",
    status_update: "/master/update-status",
    status_delete: "/master/delete-status"
  },
  LeaveEntry: {
    leave_list: "/leave/list-leave-entry",
    create_leave: "/leave/create-leave-entry",
    get_by_id: "/leave/get-leave-entry",
    update_leave: "/leave/update-leave-entry",
    delete_message: "/leave/delete-message-leave-entry",
    delete_leave: "/leave/delete-leave-entry",

  },
  Attendance: {
    dashboard: "/attendance/dashboard",
    dashboardList: "/attendance/dashboard-list",
    generateAttendance: "/attendance/create-attendance",
  },
  Approval: {
    leave_approval: "/leave/leave-entry-approval",
    manual_approval: "/attendance/manual-approval",
  },
  EmployeeMaster: {
    employee_master: "/master/create-employee",
    fetch_employee: "/master/list-employee",
    get_employee: "/master/get-employee",
    update_employee: "/master/update-employee",
    delete_message_employee: "/master/delete-message-employee",
    delete_employee: "/master/delete-employee",
  }

};

